import { Gender } from '@zorro/clients';
import { formatSsn } from '@zorro/common-utils';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { DateUtilInstance, formatDateEnLocale } from '@zorro/shared/formatters';
import { YesNo, insuredSubtypeLabelConfig } from '@zorro/types';
import { Text } from '@zorro/web/design-system';

import { InsuredDependentFormFields } from '../DependentsFormInputs';
import { INSURED_FORM_ADDRESS_TOOLTIP_LABEL } from '../InsuredFormUtils';
import { InsuredSpouseFormFields } from '../SpouseFormInputs';
import { calculateAgeAtPlanStart } from './ReadonlyInsuredFormUtils';
import { ReadonlyValuesGrid } from './ReadonlyValuesGrid';

type Props = {
  fields: InsuredSpouseFormFields | InsuredDependentFormFields;
  targetEnrollmentDate?: DateUtilInstance;
  withAgeAtPlanStart?: boolean;
  isEmployeeMode?: boolean;
};

export const ReadonlyDependentForm = ({
  fields,
  targetEnrollmentDate,
  withAgeAtPlanStart = false,
  isEmployeeMode = false,
}: Props) => {
  const {
    firstName,
    lastName,
    dateOfBirth,
    subtype,
    gender,
    isSmoker,
    isPregnant,
    ssn,
    residentialAddress,
  } = fields;

  const ageAtPlanStart = calculateAgeAtPlanStart(
    dateOfBirth,
    targetEnrollmentDate
  );

  return (
    <ReadonlyValuesGrid
      items={[
        { id: 'member-info', title: 'Member info' },
        {
          id: 'firstName',
          label: 'First name',
          value: firstName,
          masked: true,
          copyValue: firstName,
        },
        {
          id: 'lastName',
          label: 'Last name',
          value: lastName,
          masked: true,
          copyValue: lastName,
        },
        {
          id: 'dateOfBirth',
          label: 'Date of birth',
          value: formatDateEnLocale(dateOfBirth),
          masked: true,
          copyValue: formatDateEnLocale(dateOfBirth),
        },
        ...(withAgeAtPlanStart
          ? [
              {
                id: 'ageAtPlanStart',
                label: 'Age at plan start',
                value: ageAtPlanStart?.toString(),
              },
            ]
          : []),
        {
          id: 'relationship',
          label: 'Relationship',
          value: subtype && insuredSubtypeLabelConfig[subtype],
        },
        {
          id: 'gender',
          label: 'Gender',
          value: gender === Gender.MALE ? 'Male' : 'Female',
        },
        {
          id: 'smoker',
          label: 'Smoker',
          value: isSmoker === YesNo.YES ? 'Yes' : 'No',
        },
        ...(gender === Gender.FEMALE
          ? [
              {
                id: 'pregnant',
                label: 'Pregnant',
                value: isPregnant === YesNo.YES ? 'Yes' : 'No',
              },
            ]
          : []),
        {
          id: 'ssn',
          label: 'SSN',
          value: formatSsn(ssn),
          isSensitive: true,
          masked: true,
          copyValue: formatSsn(ssn),
        },
        {
          id: 'residential-address',
          label: (
            <Tooltip
              label={INSURED_FORM_ADDRESS_TOOLTIP_LABEL}
              disabled={!isEmployeeMode}
              position="top"
              maw={400}
            >
              <Text inherit c="inherit">
                Residential address
              </Text>
            </Tooltip>
          ),
          value: residentialAddress,
          span: 12,
          masked: true,
        },
      ]}
    />
  );
};
